import type { BloodGlucoseFormContext } from "@/model/BloodGlucoseFormContext"
import type { ObservationStats } from "@/model/BloodGlucoseObservations"
import { ROUTES, buildPath } from "@/routes"
import { DateTimeFormatter, LocalDate } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { useNavigate } from "react-router"
import type { LatestReading } from "../dashboardData"
import { isPastDashboardDate } from "../dashboardData"
import MeasurementCell from "./MeasurementCell"

type MeasurementGridProps = {
  readings: LatestReading[]
  statistics: Partial<Record<BloodGlucoseFormContext, ObservationStats>>
}

type Column = { context: BloodGlucoseFormContext; label: string }

const COLUMNS: Column[] = [
  { context: "fasting", label: "Fasting" },
  { context: "breakfast", label: "Breakfast" },
  { context: "lunch", label: "Lunch" },
  { context: "dinner", label: "Dinner" },
]

const DAY_FORMAT = DateTimeFormatter.ofPattern("EEE dd").withLocale(Locale.US)

const MeasurementGrid = ({ readings, statistics }: MeasurementGridProps) => {
  const navigate = useNavigate()

  return (
    <table
      className="mt-4 w-full text-center text-[12px] font-semibold text-primary-contrast"
      style={{
        borderCollapse: "separate",
        borderSpacing: "6px",
        tableLayout: "fixed",
      }}
    >
      <colgroup>
        {/* Date label column. The cards row above ("median measurements")
            stays inside this same colgroup so each card column lines up
            exactly with its corresponding daily column below. */}
        <col style={{ width: "56px" }} />
        {COLUMNS.map((column) => (
          <col key={column.context} />
        ))}
      </colgroup>
      <tbody>
        <tr>
          <td />
          {COLUMNS.map(({ context, label }) => {
            const median = statistics[context]?.medianBloodGlucoseInMilligramsPerDeciliter
            return (
              <td key={`card-${context}`} className="p-0 align-top">
                <div className="rounded-[12px] border border-[#E3E3E3] bg-[#EFFAF9] px-2 py-[11px] text-center">
                  <p className="mb-1 text-[14px] font-medium text-primary-contrast">{label}</p>
                  <p className="text-[20px] font-semibold text-primary-contrast">
                    {median != null ? Math.round(median) : "-"}
                  </p>
                </div>
              </td>
            )
          })}
        </tr>
        <tr>
          <td />
          {COLUMNS.map((column) => (
            <td
              key={`head-${column.context}`}
              className="pb-1 pt-2 text-center text-[12px] font-semibold text-primary-contrast"
            >
              {column.label}
            </td>
          ))}
        </tr>
        {readings.map((row) => {
          const date = LocalDate.parse(row.date)
          return (
            <tr key={row.date}>
              <td className="whitespace-nowrap pr-2 text-left text-[12px] font-semibold text-primary-contrast">
                {date.format(DAY_FORMAT)}
              </td>
              {COLUMNS.map((column) => {
                const reading = row[column.context]
                return (
                  <td key={column.context} className="p-0">
                    <MeasurementCell
                      value={reading?.reading}
                      classification={reading?.classification}
                      onClick={
                        reading?.readingId && isPastDashboardDate(row.date)
                          ? () =>
                              navigate(
                                buildPath(ROUTES.MEASUREMENT.EDIT, {
                                  measurementId: reading.readingId ?? "",
                                }),
                                { state: { initialTab: "overview" } },
                              )
                          : undefined
                      }
                    />
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default MeasurementGrid
